import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImportModal.module.css";
import { IoIosClose } from "react-icons/io";
import { fetchAdminContactsFile, fetchContactsFile, uploadAdminContactsFile, uploadContactsFile } from "../../../Services/contacts";
import { useDispatch, connect } from "react-redux";
import Loading from "../../Loading/Loading";
import moment from "moment";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";
import Backdrop from "../../Backdrop/Backdrop";
import { themeColors } from "../../theme";
import CSVGenerator from "./CSVGenerator";
import "../../../theme-vars.css";
import { useMediaQuery, useTheme } from "@mui/material";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import { Circles } from "react-loader-spinner";


/* ------------------------------------------------------------------
   Component Props
   ------------------------------------------------------------------ */
type props = {
  open: boolean;
  close: () => void;
  organization_id: string;
  history: any;
  usersList: any[];
  user: any;
  conatctHeader: any;
  theme: any;
};

interface DataObject {
  [key: string]: any; // define your data object interface
}

const AdminContactModal: FunctionComponent<props> = ({
  open,
  close,
  organization_id,
  history,
  usersList,
  user,
  conatctHeader,
  theme
}) => {
  /* --------------------------------------------------------------
STATES FOR SELECTED FIELDS
-------------------------------------------------------------- */
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  /** ----------------------------------------
 * Prepare CSV template header list
 * ---------------------------------------- */
  const filtervalues = ["customer_name", "country_code", "contact_no", "alternate_no", "email", "stage", "contact_owner_email", "lead_source", "budget", "call_back_reason", "project", "property_type", "property_sub_type", "property_stage", "location", "lost_reason", "not_int_reason", "other_lost_reason", "other_not_int_reason", "next_follow_up_type", "next_follow_up_date_time", "created_by", "created_at", "lead_assign_time", "stage_change_at", "addset", "campaign", "notes", "lead_type", "field_one", "field_two", "field_three", "field_four", "field_five", "field_six"];
  const reqData = conatctHeader.filter((obj: any) => filtervalues.includes(obj.label));

  /** CSV template header list */
  const dbData = [...reqData, { "label": "notes", "value": "Notes" }]
  const headerData = dbData.map((item: any) => item.value);
  const contactHeaderData: DataObject[] = [];
  contactHeaderData.push(headerData);
  const [load, setLoad] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const dispatcher = useDispatch();

  /** -------------------------------------------------
    * Fetch Upload Logs from Firestore
    * ------------------------------------------------- */
  useEffect(() => {
    if (organization_id) {
      const unsub = fetchAdminContactsFile(
        organization_id,
        (data: any) => setUploadedFiles(data),
        dispatcher
      );
      return () => {
        unsub();
      };
    }
  }, [organization_id]);

  /** -------------------------------------------------
   * Upload Handler
   * ------------------------------------------------- */
  const upload = async (file: any) => {
    setLoad(true);
    uploadAdminContactsFile(
      file,
      organization_id,
      dispatcher,
      (data: boolean) => setLoad(data),
      user.user_first_name + " " + user.user_last_name
    );
  };

  /** -----------------------------
 * Table Theme Styles
 * ----------------------------- */
  const tdStyles = {
    backgroundColor: theme ? themeColors.tdBackgroundColor : themeColors.tdBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  }

  const thStyles = {
    backgroundColor: theme ? themeColors.thBackgroundColor : themeColors.thBackgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark
  }

  /** -----------------------------
 * Modal Theme Styling
 * ----------------------------- */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "10px 20px",
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  /** -----------------------------
   * Render UI
   * ----------------------------- */
  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >
        <div className={styles.child}>

          {/* ----------------------
              Heading
          ------------------------ */}
          <h5 style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-large)", paddingTop: isMobile ? "unset" : "15px" }}>Import Data for "Contact Form"</h5>

          {/* ----------------------
              Upload History Table
          ------------------------ */}
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead className={styles.head} style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}>
                <tr>
                  <th className={styles.th} style={thStyles}>Sno.</th>
                  <th className={styles.th} style={thStyles}>Request Id</th>
                  <th className={styles.th} style={thStyles}>Created At</th>
                  <th className={styles.th} style={thStyles}>Status</th>
                  <th className={styles.th} style={thStyles}>Upload Count</th>
                  <th className={styles.th} style={thStyles}>Uploaded File</th>
                  <th className={styles.th} style={thStyles}>Processed File</th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles.map((item: any, index: number) => (
                  <tr key={index} className={styles.no}>
                    <td className={styles.td} style={tdStyles}>{index + 1}</td>
                    <td className={styles.td} style={tdStyles}>{item.request_id}</td>
                    <td className={styles.td} style={tdStyles}>
                      {moment(item.created_at.toDate()).format("MM/DD/YY HH:mm")}
                    </td>
                    <td className={styles.td} style={tdStyles}>{item.status}</td>
                    <td className={styles.td} style={tdStyles}>{item.uploadCount}</td>
                    <td className={styles.td} style={tdStyles}>
                      <a href={item.fileUrl}>Uploaded File</a>
                    </td>
                    <td className={styles.td} style={tdStyles}>
                      {item.responseURL !== "" && (
                        <a href={item.responseURL}>Processed File</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ----------------------
              Action Buttons
          ------------------------ */}
          <div className={styles.selectBox}>
            <div style={{ marginRight: 8 }}>
              <StyledCancelButton onClick={close}>
                Cancel
              </StyledCancelButton>
            </div>
            <div style={{ marginRight: 8 }}><CSVGenerator data={contactHeaderData} /></div>
            <div>
              <input
                id="file-input"
                className={styles.select}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e: any) => upload(e.target.files[0])}
              />
              <StyledSubmitButton
                onClick={() => document.getElementById("file-input")?.click()}
                isMobile={isMobile}
              >
                {load ? (
                  <span style={{ display: "flex", justifyContent: "center" }}>
                    <Circles color="#fff" height={20} width={20} />Upload...
                  </span>
                ) : (
                  <>Upload File</>
                )}
              </StyledSubmitButton>
            </div>
          </div>

          {/* ----------------------
              Info Text
          ------------------------ */}
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <p style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}><i className="fas fa-info-circle"></i>  Only .csv file type is supported.</p>
            <p style={{ marginTop: "-1rem", fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}><i className="fas fa-info-circle"></i>  Please ensure filling all the mandatory fields.</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

/** Map Redux store to props */
const mapStateToProps = (state: any) => {
  return {
    conatctHeader: state.contactHeaderList.data,
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(AdminContactModal);

